// @ts-strict-ignore
import isNumber from 'lodash/isNumber';
import mergeWith from 'lodash/mergeWith';

import {formatConciseDuration} from 'common/util/date';
import {
  isConversionOrRetentionRateEntry,
  isConversionOrRetentionRateMetric,
  isFunnelTimeToConvertMetric,
  isFunnelTimeToConvertShowClause,
  showClausesAreConversionOrRetentionRateMetric,
} from 'common/report/insights/models/utils';
import {
  InsightsBookmarkParams,
  InsightsChartParams,
  InsightsQueryResults,
  ShowClause,
} from 'common/report/insights/util/types';
import {EventPropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {DataFormatType} from 'common/components/data-table/types';
import {TableRow} from 'common/components/table/types';
import {PropertyDisplayOptions} from 'apps/lexicon/src/models/property-definition';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {LineChartYAxisOptions} from 'common/charts/visualization/line-chart/types';
import {EntryWithDisplay} from 'common/query-builder/query-entry-section/types';

type MetricFormatterFunction = (value: number) => number | string;

function coreMetricFormatterFunction<T>(
  paramsOrClause: T | null,
  isConversionOrRetentionCheck: (paramsOrClause: T) => boolean,
  isFunnelTimeToConvertCheck: (paramsOrClause: T) => boolean,
): MetricFormatterFunction {
  return (value: number): number | string => {
    if (!paramsOrClause) {
      return value;
    }
    if (isConversionOrRetentionCheck(paramsOrClause)) {
      // Just multiply by 100. We'll delegate to code higher up to handle potential
      // user-specified overrides on decimal precision, etc.
      return value * 100;
    } else if (isFunnelTimeToConvertCheck(paramsOrClause)) {
      return formatConciseDuration(value);
    }
    return value;
  };
}

export function metricFormatterFunctionForBookmarkParams(bookmarkParams: InsightsBookmarkParams | InsightsChartParams) {
  return coreMetricFormatterFunction(bookmarkParams, isConversionOrRetentionRateMetric, isFunnelTimeToConvertMetric);
}

export function metricFormatterFunctionForShowClause(showClause: ShowClause) {
  return coreMetricFormatterFunction(
    showClause,
    (c) => showClausesAreConversionOrRetentionRateMetric([c]),
    isFunnelTimeToConvertShowClause,
  );
}

function coreGetMetricFormattingDefaults<T>(
  paramsOrClause: T,
  isConversionOrRetentionCheck: (paramsOrClause: T) => boolean,
): Partial<PropertyDisplayOptions> {
  if (isConversionOrRetentionCheck(paramsOrClause)) {
    return {
      suffix: `%`,
    };
  }
  return {};
}

/**
 * Given a bookmark or chart params object, returns the appropriate defaults
 * for formatting metrics to be displayed.
 * @param bookmarkParams
 * @returns
 */
export function getMetricFormattingDefaultsFromChartParams(
  bookmarkParams: InsightsBookmarkParams | InsightsChartParams,
): Partial<PropertyDisplayOptions> {
  return coreGetMetricFormattingDefaults(bookmarkParams, isConversionOrRetentionRateMetric);
}

export function getMetricFormattingDefaultsFromShowClause(showClause: ShowClause): Partial<PropertyDisplayOptions> {
  return coreGetMetricFormattingDefaults(showClause, (c) => showClausesAreConversionOrRetentionRateMetric([c]));
}

export function getMetricFormattingDefaultsFromQueryEntry(entry: EntryWithDisplay): Partial<PropertyDisplayOptions> {
  return coreGetMetricFormattingDefaults(entry, isConversionOrRetentionRateEntry);
}

export function getMetricKeyToShowClauseMap(
  bookmarkParams: InsightsBookmarkParams | InsightsChartParams,
  results: InsightsQueryResults,
): Record<string, ShowClause> {
  return bookmarkParams.sections.show.reduce(
    (acc, section, index) => {
      const metricKey = results.meta.report_sections.show[index].metric_key;
      acc[metricKey] = section;
      return acc;
    },
    {} as Record<string, ShowClause>,
  );
}

export const getLineChartYAxisOptionsFromPropertyDisplayOptions = (
  propertyDisplayOptions: Partial<PropertyDisplayOptions>,
): LineChartYAxisOptions => {
  return {
    axisUnit: {
      prefix: propertyDisplayOptions.prefix,
      suffix: propertyDisplayOptions.suffix,
      precision: propertyDisplayOptions.precision,
    },
  };
};

/**
 * Pulls out the display options defined for the relevant property and metric
 * and then returns the final values to be used for formatting.
 * @param showClause
 * @param eventPropertyDefinitionsStore
 * @param defaultOptions Optiona default values to start with and then have overridden
 * by property values.
 * @param overrideOptions Optional values to override all other values.
 * @returns
 */
export function getFinalDisplayOptions(
  showClause: ShowClause,
  eventPropertyDefinitionsStore: EventPropertyDefinitionsStore,
  defaultOptions: Partial<PropertyDisplayOptions> = {},
  overrideOptions: Partial<PropertyDisplayOptions> = {},
): Partial<PropertyDisplayOptions> {
  const options = {prefix: ``, suffix: ``, abbrev: true, precision: null, ...defaultOptions};
  function merger(objValue, srcValue, key) {
    // Only override with non empty prefix/suffix values
    if ([`prefix`, `suffix`].includes(key) && !srcValue) {
      return objValue;
    }
    // Delegate to lodash's default behavior
    return undefined;
  }
  if (showClause?.measurement && `property` in showClause.measurement && showClause.measurement.property) {
    const property = showClause.measurement.property;
    let propertyDefinition = null;
    if (property.customPropertyId) {
      propertyDefinition = customPropertiesStore.value[property.customPropertyId];
    } else {
      const propertyName = property.name;
      propertyDefinition = propertyName instanceof Array ? null : eventPropertyDefinitionsStore.value[propertyName];
    }

    mergeWith(options, propertyDefinition?.displayOptions, merger);
  }
  mergeWith(options, showClause?.display, overrideOptions, merger);

  return options;
}

/**
 * If one value in a column has a decimal format, all values in that column
 * should be formatted as decimals. This function scans all rows in a table to
 * see that the given column field has a decimal value.
 *
 * Types that already have decimals (percent, time) should be left alone.
 * @param dataFormatType
 * @param rows
 * @param field
 * @returns Returns the correct data format type, updated to decimal if needed.
 */
export function checkForDecimalDataType(
  dataFormatType: DataFormatType,
  rows: TableRow[],
  field: string,
): DataFormatType {
  if ([DataFormatType.Percent, DataFormatType.Time].includes(dataFormatType)) {
    return dataFormatType;
  }

  return rows.some((row) => {
    const value = row[field];
    return isNumber(value) && value % 1 !== 0;
  })
    ? DataFormatType.Decimal
    : dataFormatType;
}
